import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useRBAC } from '@/contexts/rbac-context';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { APPROVAL_MENU_KEY } from '@/lib/approval-client';
import { ApprovalAdminScreen } from '@/components/features/approval/approval-admin';
import { ApprovalDetailScreen } from '@/components/features/approval/approval-detail';
import { ApprovalListScreen } from '@/components/features/approval/approval-list';
import { PROGRAM_TITLE, useApprovalNav, useInboxPendingCounts } from '@/components/features/approval/approval-common';
import type { ApprovalProgram } from '@/types/approval';
import { ChevronDown, FileText, Home, Inbox, Plus, Settings, Stamp } from 'lucide-react';

/**
 * 전자결재 SPA. 탭 쉘(main-page) 밖에서 새 창으로 뜨고 URL 이 그대로 화면을 가리킨다 (핸드오프 §1.4).
 * 알림 딥링크 `/approvals/{id}` 가 여기 상세 라우트로 착지한다.
 */
export function ApprovalPage() {
  useDocumentTitle('전자결재');
  return (
    <div className='flex h-screen w-full bg-background'>
      <ApprovalRail />
      <div className='flex min-w-0 flex-1 flex-col'>
        <MobileNav />
        <div className='mx-auto flex min-h-0 w-full max-w-[1600px] flex-1 flex-col overflow-auto p-4'>
          <Routes>
            <Route index element={<ApprovalHome />} />
            <Route path='requests/:program' element={<ApprovalListScreen box='requests' />} />
            <Route path='inbox/:program' element={<ApprovalListScreen box='inbox' />} />
            <Route path='admin' element={<ApprovalAdminScreen />} />
            <Route path=':id' element={<ApprovalDetailScreen showBack />} />
            <Route path='*' element={<ApprovalHome />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

/** 접근 가능한 첫 화면으로 보낸다. 결재함을 먼저 본다 (대기 건이 있는 쪽) */
function ApprovalHome() {
  const { canAccessMenu } = useRBAC();
  const nav = useApprovalNav();
  const entry = ['a330', 'a340', 'b210', 'b220'].find(canAccessMenu);
  if (entry) {
    const { box, program } = APPROVAL_MENU_KEY[entry];
    return <Navigate to={`${nav.root}/${box}/${program}`} replace />;
  }
  if (canAccessMenu('approval-admin')) {
    return <Navigate to={`${nav.root}/admin`} replace />;
  }
  return (
    <div className='p-12 text-center text-sm text-muted-foreground'>
      접근 가능한 전자결재 화면이 없습니다. 관리자에게 역할세트 권한을 요청하세요.
    </div>
  );
}

/** 화면 묶음. 결재함/기안함을 나눠 두지 않으면 같은 이름이 네 번 나열된다 */
function useNavGroups() {
  const { canAccessMenu } = useRBAC();
  const nav = useApprovalNav();

  const boxItems = (keys: string[]) =>
    keys.filter(canAccessMenu).map(key => {
      const { box, program } = APPROVAL_MENU_KEY[key];
      return { key, program: program as ApprovalProgram | null, title: PROGRAM_TITLE[program], path: `${nav.root}/${box}/${program}` };
    });
  return [
    { box: 'inbox', title: '결재함', short: '결재', icon: Inbox, items: boxItems(['a330', 'a340']) },
    { box: 'requests', title: '기안함', short: '기안', icon: FileText, items: boxItems(['b210', 'b220']) },
    {
      box: null,
      title: '관리',
      short: '',
      icon: Settings,
      items: canAccessMenu('approval-admin')
        ? [{ key: 'approval-admin', program: null, title: '전자결재 관리', path: `${nav.root}/admin` }]
        : [],
    },
  ].filter(g => g.items.length > 0);
}

function ApprovalRail() {
  const groups = useNavGroups();
  const nav = useApprovalNav();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inboxPrograms = groups.find(g => g.box === 'inbox')?.items.map(i => i.program as ApprovalProgram) ?? [];
  const pending = useInboxPendingCounts(inboxPrograms);

  return (
    <aside className='hidden w-52 shrink-0 flex-col bg-sidebar text-sidebar-foreground md:flex'>
      {/* 결재는 새 창이라 여기서 벗어날 길이 없었다. 로고를 메인 앱 홈 링크로 둔다 */}
      <a
        href={nav.appHome}
        className='flex items-center gap-2 border-b border-sidebar-border/20 px-4 py-3 font-semibold'
        title='PetroHQ 홈으로'
      >
        <Stamp className='size-5' />
        전자결재
      </a>
      <NewRequestButton drafts={groups.find(g => g.box === 'requests')?.items ?? []} />
      <nav className='flex-1 space-y-4 overflow-y-auto p-2'>
        {groups.map(group => (
          <div key={group.title}>
            <div className='flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wide opacity-60'>
              <group.icon className='size-3.5' />
              {group.title}
            </div>
            {group.items.map(item => (
              <button
                key={item.key}
                type='button'
                onClick={() => navigate(item.path)}
                aria-current={pathname === item.path ? 'page' : undefined}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-sidebar-accent ${
                  pathname === item.path ? 'bg-sidebar-accent font-medium' : ''
                }`}
              >
                <span className='break-keep'>{item.title}</span>
                {!!(group.box === 'inbox' && item.program && pending[item.program]) && (
                  <span className='rounded-full bg-sidebar-primary px-1.5 text-[11px] font-semibold tabular-nums text-sidebar-primary-foreground'>
                    {pending[item.program]}
                  </span>
                )}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <a
        href={nav.appHome}
        className='flex items-center gap-2 border-t border-sidebar-border/20 px-4 py-3 text-sm opacity-80 hover:opacity-100'
      >
        <Home className='size-4' />
        PetroHQ 홈으로
      </a>
    </aside>
  );
}

/** 레일 상단의 등록 버튼. 어느 화면을 보고 있든 한 번에 상신으로 간다 (기안함 화면이 `?new=1` 로 다이얼로그를 연다) */
/** 레일이 어두워 기본 primary(거의 검정) 버튼이 묻힌다 - 배지와 같은 레일 강조색 */
const CTA_CLASS = 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90';

function NewRequestButton({ drafts }: { drafts: { key: string; title: string; path: string }[] }) {
  const navigate = useNavigate();
  if (drafts.length === 0) return null;
  const open = (path: string) => navigate(`${path}?new=1`);
  if (drafts.length === 1) {
    return (
      <div className='p-2'>
        <Button className={`w-full ${CTA_CLASS}`} onClick={() => open(drafts[0].path)}>
          <Plus className='mr-1 size-4' />
          승인요청 등록
        </Button>
      </div>
    );
  }
  return (
    <div className='p-2'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className={`w-full ${CTA_CLASS}`}>
            <Plus className='mr-1 size-4' />
            승인요청 등록
            <ChevronDown className='ml-auto size-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='w-48'>
          {drafts.map(d => (
            <DropdownMenuItem key={d.key} onSelect={() => open(d.path)}>
              {d.title}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** 레일을 숨기는 좁은 화면용 가로 네비 */
function MobileNav() {
  const groups = useNavGroups();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const nav = useApprovalNav();

  return (
    <header className='flex items-center gap-2 border-b px-3 py-2 md:hidden'>
      <a href={nav.appHome} className='flex shrink-0 items-center gap-1 font-semibold' title='PetroHQ 홈으로'>
        <Stamp className='size-5 text-primary' />
      </a>
      <nav className='flex items-center gap-1 overflow-x-auto'>
        {groups.flatMap(group =>
          group.items.map(item => (
            <Button
              key={item.key}
              size='sm'
              variant={pathname === item.path ? 'secondary' : 'ghost'}
              className='whitespace-nowrap'
              onClick={() => navigate(item.path)}
            >
              {group.short ? `${group.short} ${item.title}` : item.title}
            </Button>
          ))
        )}
      </nav>
    </header>
  );
}
